import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import { Alert } from '@pms-digital/design-system';

/**
 * « Supprimer définitivement ce NPC ? » (npc.md §3.5, N14, N15, H25; maquette `Fiche`, planche 3).
 *
 * The box of the one gesture of the section that does not come back, and the one that holds an
 * erasure request for a person who gave an NPC (§4.2). It says, before the click:
 *
 *   - what leaves at once - the NPC and ALL its versions, and the benches it leaves;
 *   - what becomes of its replays - they go to the trash of Sessions, purged in thirty days like any
 *     session and restorable until then, with no link left to this NPC, and one that runs is
 *     interrupted first (H25);
 *   - that it does not come back, and that « Retirer » is the way to only hide it.
 *
 * The counts are the detail's (`versions`, `replayCount`) and the benches are the referents' - the
 * same read the aside shows - so the box names what the screen already knows; what the server then
 * answers (`npcDeletedSchema`) is what happened, and the page reads it back.
 *
 * `VersionedDialog` puts the focus on « Annuler »: Enter must never be the key that erases.
 */

export interface NpcDeleteDialogProps {
  name: string;
  versionCount: number;
  replayCount: number;
  /** The benches the NPC leaves, or `null` while they are not read (the box then cannot name them). */
  benches: readonly { readonly id: string; readonly name: string }[] | null;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function plural(count: number, one: string, many: string): string {
  return count > 1 ? many : one;
}

export function NpcDeleteDialog({
  name,
  versionCount,
  replayCount,
  benches,
  busy,
  error,
  onConfirm,
  onCancel,
}: NpcDeleteDialogProps) {
  return (
    <VersionedDialog
      data-testid="npc-boite-suppression"
      title="Supprimer définitivement ce NPC ?"
      description={name}
      confirmLabel={
        replayCount === 0
          ? 'Supprimer le NPC'
          : `Supprimer le NPC et mettre ${replayCount > 1 ? `ses ${replayCount} rejeux` : 'son rejeu'} à la corbeille`
      }
      confirmTone="destructive"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <VersionedDialogSection title="Ce qui part tout de suite">
        <p data-testid="npc-suppression-versions" className="text-gray-800 text-small">
          Le NPC et ses{' '}
          <strong className="font-semibold">
            {versionCount} {plural(versionCount, 'version', 'versions')}
          </strong>{' '}
          : sa fiche, ses sources, son historique.{' '}
          {benches === null
            ? 'Il quitte aussi les bancs où il figure.'
            : benches.length === 0
              ? 'Aucun banc ne le porte.'
              : `Il quitte aussi ${plural(benches.length, 'le banc', 'les bancs')} où il figure : ${benches
                  .map((bench) => `« ${bench.name} »`)
                  .join(', ')}.`}
        </p>
      </VersionedDialogSection>

      <VersionedDialogSection
        title={
          replayCount === 0
            ? 'Ses rejeux'
            : `Ses ${replayCount} ${plural(replayCount, 'rejeu', 'rejeux')}`
        }
      >
        {replayCount === 0 ? (
          <p data-testid="npc-suppression-rejeux" className="text-gray-800 text-small">
            Aucun rejeu ne l’a joué : rien d’autre ne part.
          </p>
        ) : (
          <p data-testid="npc-suppression-rejeux" className="text-gray-800 text-small">
            {replayCount > 1 ? 'Ils vont' : 'Il va'}{' '}
            <strong className="font-semibold">à la corbeille</strong> de Sessions et y{' '}
            {replayCount > 1 ? 'seront' : 'sera'}{' '}
            <strong className="font-semibold">
              purgé{replayCount > 1 ? 's' : ''} dans 30 jours
            </strong>
            . D’ici là, {replayCount > 1 ? 'ils se restaurent' : 'il se restaure'}
            {replayCount > 1 ? ' un par un' : ''}, sans plus de lien vers ce NPC. Un rejeu en cours
            est interrompu d’abord.
          </p>
        )}
      </VersionedDialogSection>

      <Alert tone="danger" title="Ce geste ne se défait pas.">
        Pour seulement cacher ce NPC, utilisez plutôt « Retirer » : rien n’est effacé.
      </Alert>
    </VersionedDialog>
  );
}
